/**
 * O que faz: Renderiza o grid dos cards principais do dashboard com suporte a clique para drill-down e badges de tendência.
 * Onde usa: Componente Dashboard principal.
 * Conexões: MetricCard e tipos do hook de dashboard.
 * Tabelas: Exibe métricas agregadas calculadas a partir da tabela leads e dados relacionados.
 * Data: 11/06/2026
 */

import { MetricCard } from './MetricCard';
import { Users, Calendar, TrendingUp, DollarSign, CheckCircle, Megaphone, Link, MessageSquare } from 'lucide-react';
import { DashboardMetrics as DashboardMetricsType } from '@/hooks/dashboard/types';

interface DashboardMetricsProps {
  dashboardData: DashboardMetricsType | null;
  onTotalContatosClick?: () => void;
  onLeadsAnunciosClick?: () => void;
  onLeadsLinksClick?: () => void;
  onFaturamentoClick?: () => void;
  isUpdating?: boolean;
  /** Mostra estado local enquanto chat_mensagens termina sem bloquear os demais KPIs. */
  atendimentoLoading?: boolean;
  /** Evita apresentar zero como se fosse resultado quando a consulta de atendimento falha. */
  atendimentoError?: boolean;
}

// Converte a variação numérica em texto/tipo do badge.
// Variações nulas ou zeradas não geram badge para não poluir o card com "0%".
const buildTrend = (variacao: number | undefined | null) => {
  if (variacao === undefined || variacao === null || !isFinite(variacao) || variacao === 0) {
    return {};
  }
  return {
    change: `${Math.abs(Math.round(variacao))}%`,
    changeType: variacao > 0 ? ('positive' as const) : ('negative' as const),
  };
};

export const DashboardMetrics = ({
  dashboardData,
  onTotalContatosClick,
  onLeadsAnunciosClick,
  onLeadsLinksClick,
  onFaturamentoClick,
  isUpdating = false,
  atendimentoLoading = false,
  atendimentoError = false,
}: DashboardMetricsProps) => {
  const metrics = dashboardData ? [
    {
      title: 'Total de Contatos',
      value: dashboardData.totalContatos.toString(),
      icon: Users,
      onClick: onTotalContatosClick,
      clickable: !!onTotalContatosClick,
      ...buildTrend(dashboardData.variacaoContatos)
    },
    {
      title: 'Leads de Anúncios',
      value: dashboardData.leadsAnuncios.toString(),
      icon: Megaphone,
      onClick: onLeadsAnunciosClick,
      clickable: !!onLeadsAnunciosClick,
      ...buildTrend(dashboardData.variacaoLeadsAnuncios)
    },
    {
      title: 'Leads de Links',
      value: dashboardData.leadsLinksPersonalizados.toString(),
      icon: Link,
      onClick: onLeadsLinksClick,
      clickable: !!onLeadsLinksClick,
      ...buildTrend(dashboardData.variacaoLeadsLinksPersonalizados)
    },
    {
      title: 'Mensagens',
      value: atendimentoError
        ? '—'
        : (dashboardData.mensagensEnviadas + dashboardData.mensagensRecebidas).toString(),
      icon: MessageSquare,
      isLoading: atendimentoLoading,
      details: atendimentoError
        ? [{ label: 'indisponível', value: '—' }]
        : [
            { label: 'enviadas', value: dashboardData.mensagensEnviadas.toString() },
            { label: 'recebidas', value: dashboardData.mensagensRecebidas.toString() }
          ]
    },
    {
      title: 'Consultas Agendadas',
      value: dashboardData.consultasAgendadas.toString(),
      icon: Calendar,
      ...buildTrend(dashboardData.variacaoConsultas)
    },
    {
      title: 'Consultas Realizadas',
      value: dashboardData.consultasRealizadas.toString(),
      icon: CheckCircle,
      ...buildTrend(dashboardData.variacaoConsultasRealizadas)
    },
    {
      title: 'Taxa de Conversão',
      value: `${dashboardData.taxaConversao}%`,
      icon: TrendingUp,
      ...buildTrend(dashboardData.variacaoConversao)
    },
    {
      title: 'Faturamento Realizado',
      value: `R$ ${dashboardData.faturamentoRealizado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      // Reaproveita o clique e o teclado do MetricCard para abrir os registros do mesmo período.
      onClick: onFaturamentoClick,
      clickable: !!onFaturamentoClick,
      // O detalhamento (consultas pagas, vendas do CRM, formas) vive no modal;
      // exibi-lo no card esticava todas as linhas da grade e criava vazio nos demais cards.
      ...buildTrend(dashboardData.variacaoFaturamento)
    }
  ] : [];

  return (
    // 2 colunas no mobile (cards compactos), 4 em telas grandes.
    // Fileiras fixas dividem a altura da coluna igualmente (base alinhada com o
    // "Tempo médio de resposta"); cada card preenche a fileira (h-full) e o
    // espaçador interno distribui a sobra entre valor e base, sem vazio embaixo.
    <div className="grid h-full grid-cols-2 lg:grid-cols-3 lg:grid-rows-3 xl:grid-cols-4 xl:grid-rows-2 gap-3 sm:gap-4">
      {metrics.map((metric) => (
        <MetricCard key={metric.title} {...metric} isUpdating={isUpdating} />
      ))}
    </div>
  );
};
